import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Stack,
  IconButton,
  DialogContentText
} from '@mui/material';
import {
  EnvelopeSimple,
  GoogleLogo,
  MicrosoftOutlookLogo,
  CheckCircle,
  X,
  Trash
} from '@phosphor-icons/react';
import api from '../../../utils/api';
import IntegrationPrivacyConsentDialog from '../Integrations/IntegrationPrivacyConsentDialog';

const PROVIDER_META = {
  google: { label: 'Google / Gmail', Icon: GoogleLogo },
  microsoft: { label: 'Microsoft 365 / Outlook', Icon: MicrosoftOutlookLogo }
};

const MailConnectionSettings = ({ open, onClose, businessId, canManage = true }) => {
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connection, setConnection] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [signature, setSignature] = useState('');
  const [savingSignature, setSavingSignature] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [privacyConsentProvider, setPrivacyConsentProvider] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/api/mail-sync/status', { params: { businessId } });
      setConnection(response.data.connection || null);
      setSignature(response.data.connection?.emailSignature || '');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load mail connection');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (!open) return;
    fetchStatus();

    const params = new URLSearchParams(window.location.search);
    const mailSync = params.get('mailSync');
    if (mailSync === 'connected') {
      setSuccess(`Connected ${params.get('email') || 'mailbox'} successfully.`);
    } else if (mailSync === 'error') {
      setError(`Could not connect mailbox: ${params.get('reason') || 'unknown error'}`);
    }
  }, [open, fetchStatus]);

  const handleConnect = async (provider) => {
    setError('');
    setSuccess('');
    setConnecting(provider);
    try {
      const response = await api.get(`/api/mail-sync/${provider}/auth`, { params: { businessId } });
      if (response.data?.authUrl) {
        window.location.href = response.data.authUrl;
        return;
      }
      setError('No authorisation URL returned');
      setPrivacyConsentProvider(null);
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || 'Failed to start authorisation';
      if (status === 409) {
        await fetchStatus();
      }
      setError(message);
      setPrivacyConsentProvider(null);
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async () => {
    if (!connection?._id) return;
    setDisconnecting(true);
    setError('');
    try {
      await api.delete(`/api/mail-sync/${connection._id}`);
      setSuccess('Mailbox disconnected.');
      setConnection(null);
      setDisconnectConfirmOpen(false);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to disconnect mailbox');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveSignature = async () => {
    if (!connection?._id) return;
    setSavingSignature(true);
    setError('');
    try {
      const response = await api.put(`/api/mail-sync/${connection._id}/signature`, {
        signature
      });
      setConnection(response.data.connection || connection);
      setSuccess('Email signature saved.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save email signature');
    } finally {
      setSavingSignature(false);
    }
  };

  const isConnected = useMemo(() => connection && connection.status === 'active', [connection]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EnvelopeSimple size={22} />
        <Box component="span" sx={{ flexGrow: 1 }}>Email inbox</Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : isConnected ? (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <CheckCircle size={20} color="#2e7d32" weight="fill" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Mailbox connected</Typography>
            </Stack>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {connection.emailAddress}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                size="small"
                label={PROVIDER_META[connection.provider]?.label || connection.provider}
              />
              <Chip size="small" color="success" label="Active" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              When you reply from Idotoo, it goes from this address. We do not keep a full copy of emails
              here unless you choose Save as a note on a couple record. You can disconnect any time.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Email signature
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              This text will be added to the bottom of every reply sent from this inbox.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              placeholder="Thanks, [Your business name]"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              disabled={!canManage}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveSignature}
                disabled={!canManage || savingSignature}
              >
                {savingSignature ? 'Saving…' : 'Save signature'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Pick the mailbox your couples already email. You will sign in once with Google or Microsoft.
              After that, new threads show in Messages when you open the Email tab, and your team can reply
              without hopping between apps. Replies always come from that address. To keep something on a
              couple record, use Save as a note when you are reading a thread.
            </Typography>
            <Stack spacing={1.5}>
              <Button
                variant="outlined"
                size="large"
                disabled={!canManage || connecting !== null}
                onClick={() => setPrivacyConsentProvider('google')}
                startIcon={<GoogleLogo size={20} />}
              >
                {connecting === 'google' ? 'Redirecting…' : 'Connect Gmail / Google Workspace'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                disabled={!canManage || connecting !== null}
                onClick={() => setPrivacyConsentProvider('microsoft')}
                startIcon={<MicrosoftOutlookLogo size={20} />}
              >
                {connecting === 'microsoft' ? 'Redirecting…' : 'Connect Microsoft 365 / Outlook'}
              </Button>
            </Stack>
            {!canManage && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Only the business owner or an admin can connect a mailbox.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {isConnected && (
          <Button
            color="error"
            variant="outlined"
            onClick={() => setDisconnectConfirmOpen(true)}
            disabled={disconnecting || !canManage}
            startIcon={<Trash size={18} />}
            sx={{ mr: 'auto' }}
          >
            {disconnecting ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Dialog
        open={disconnectConfirmOpen}
        onClose={() => !disconnecting && setDisconnectConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Disconnect inbox?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Idotoo will stop syncing this mailbox and remove stored access tokens. Your team will no
            longer see or send emails from this inbox in Idotoo until it is connected again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectConfirmOpen(false)} disabled={disconnecting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? 'Disconnecting…' : 'Disconnect inbox'}
          </Button>
        </DialogActions>
      </Dialog>

      <IntegrationPrivacyConsentDialog
        open={Boolean(privacyConsentProvider)}
        integration={privacyConsentProvider}
        onClose={() => setPrivacyConsentProvider(null)}
        onConfirm={() => {
          if (privacyConsentProvider) {
            handleConnect(privacyConsentProvider);
          }
        }}
        confirming={connecting === privacyConsentProvider}
      />
    </Dialog>
  );
};

export default MailConnectionSettings;
